"use client";
import { ProcessedCourse } from "@/common/course/types";
import ContentOverview from "@/components/CourseContent/ContentOverview";
import CourseCurriculum, { CourseCurriculumProps } from "./CourseCurriculum";
import AlumniTestimonials from "@/components/AlumniTestimonials";
import Instructors from "./Instructors";
import QuickFacts from "./QuickFacts";
import PaymentOptions from "./PaymentOptions";
import WhoShouldTake from "./WhoShouldTake";
import Collaboration from "./Collaboration";
import FAQs from "./FAQs";
import Cta from "./Cta";
import { cn } from "@/lib/utils";
import useExecEdDetailPageTest from "@/monarch/rules/execEdDetailPageTest/useExecEdDetailPageTest";
import { ExecEdDetailPageProps } from "@/monarch/rules/execEdDetailPageTest/types";
import useTranslations from "@/i18n/useTranslations";

export default function ExecEdBTFRedesignV7({
  course,
}: {
  course: ProcessedCourse;
}) {
  const t = useTranslations("Course");
  const tCommon = useTranslations("Common");
  const { data } = useExecEdDetailPageTest();

  const order = Object.entries(data?.redesignV7 ?? {}).reduce(
    (acc, [key, value]) => {
      if (value?.position) {
        acc[value.position] = key;
      }
      return acc;
    },
    {} as Record<number, string>,
  );

  // Sort sections by position
  const sortedSections = Object.keys(order).sort(
    (a, b) => Number(a) - Number(b),
  );

  const Container = ({
    children,
    classname,
  }: {
    children: React.ReactNode;
    classname?: string;
  }) => {
    return (
      <div className={cn("bg-gray-50", classname)}>
        <div className="w-full px-6 md:px-32 py-6 mx-auto max-w-screen-2xl">
          <div className="space-y-8">{children}</div>
        </div>
      </div>
    );
  };

  // Function to render the appropriate component for each section
  const renderSectionComponent = (positionKey: string) => {
    const sectionKey = order[Number(positionKey)];
    const sectionData = data?.redesignV7 as ExecEdDetailPageProps["redesignV7"];

    switch (sectionKey) {
      case "aboutThisCourse":
        const { description, title } = sectionData?.aboutThisCourse ?? {};
        return (
          <Container key={positionKey}>
            <ContentOverview
              fullDescription={description as string}
              title={title as string}
            />
          </Container>
        );
      case "curriculum":
        return (
          <Container key={positionKey}>
            <CourseCurriculum
              {...(sectionData?.curriculum as CourseCurriculumProps)}
            />
          </Container>
        );
      case "earnYourCredentials":
        const { description: earnDescription, title: earnTitle } =
          sectionData?.earnYourCredentials ?? {};
        return (
          <Container key={positionKey}>
            <ContentOverview
              fullDescription={earnDescription as string}
              title={earnTitle as string}
            />
          </Container>
        );
      case "testimonials":
        const testimonialData = sectionData?.testimonials;
        const monarchTestimonials = {
          title: "What our learners say",
          items:
            testimonialData?.data?.map((testimonial) => ({
              author: testimonial.author as string,
              quote: testimonial.text as string,
            })) || [],
        };

        return (
          <Container key={positionKey} classname="bg-[#f5f1ed]">
            <AlumniTestimonials
              monarchTestimonials={monarchTestimonials}
              useSlider={true}
              testimonialContainerClassName="bg-white mt-10"
              headingClassName="text-center"
              fillWithDefaults={false}
            />
          </Container>
        );
      case "instructors":
        const instructorData = sectionData?.instructors;
        return (
          <Container key={positionKey}>
            <Instructors course={course} instructors={instructorData} />
          </Container>
        );
      case "quickFacts":
        const quickFactsData = sectionData?.quickFacts;
        return (
          <Container key={positionKey}>
            <QuickFacts course={course} {...quickFactsData} />
          </Container>
        );
      case "paymentOptions":
        const paymentOptionsData = sectionData?.paymentOptions;
        return (
          <Container key={positionKey}>
            <PaymentOptions {...paymentOptionsData} />
          </Container>
        );
      case "whoShouldTakeThisCourse":
        return (
          <Container key={positionKey}>
            <WhoShouldTake
              course={course}
              {...sectionData?.whoShouldTakeThisCourse}
            />
          </Container>
        );
      case "collaboration":
        const collaborationData = sectionData?.collaboration;
        return (
          <Container key={positionKey}>
            <Collaboration course={course} {...collaborationData} />
          </Container>
        );
      case "faqs":
        const faqsData = sectionData?.faqs;
        return (
          <Container key={positionKey}>
            <FAQs course={course} {...faqsData} />
          </Container>
        );
      case "growCarreerButton":
        return (
          <Container key={positionKey}>
            <Cta
              title={t("readyToGrowSkills")}
              buttonLabel={tCommon("startNow")}
            />
          </Container>
        );
      case "advanceCareerButton":
        return (
          <Container key={positionKey}>
            <Cta
              title={t("readyToAdvanceCareer")}
              buttonLabel={tCommon("startNow")}
            />
          </Container>
        );
      default:
        return null;
    }
  };

  return (
    <div className="py-12">{sortedSections.map(renderSectionComponent)}</div>
  );
}
